import { useEffect, useRef } from 'react';
import { useLanguageContext } from '@/i18n/LanguageContext';

const PURCHASE_URL = 'https://www.amazon.co.jp/dp/4058027606';
const COVER_ALT_JA = '『執事のマナーと教養図鑑』全日本執事協会監修・学研の書影';
const COVER_ALT_EN = "Cover of Gakken's A British Butler's Guide to Manners and Cultures, supervised by the Japan Butler Association";
const JS_PLACEMENT_MIN_WIDTH = 1001;
const GAP_ABOVE_BAR = 24;
const GAP_BELOW_BLOCKER = 12;
const GAP_X = 8;
const HEADER_FALLBACK_MIN_TOP = 152;

export default function HeroBookBadge() {
  const { lang } = useLanguageContext();
  const ref = useRef<HTMLElement>(null);
  const isEn = lang === 'en';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const hero = el.closest<HTMLElement>('.tjba-hero');
    if (!hero) return;

    let raf = 0;
    let disposed = false;
    const place = () => {
      if (disposed) return;
      el.style.removeProperty('--tjba-hb-cover-w');
      el.style.removeProperty('--tjba-hb-card-w');

      const computePlacement = () => {
        const bar = document.querySelector<HTMLElement>('.fixed.bottom-0.z-40');
        const barH = bar ? bar.getBoundingClientRect().height : 70;
        const narrow = window.innerWidth < JS_PLACEMENT_MIN_WIDTH;
        const gapAboveBar = narrow ? 16 : GAP_ABOVE_BAR;
        const heroRect = hero.getBoundingClientRect();
        const heroTopDoc = heroRect.top + window.scrollY;
        const badgeH = el.offsetHeight;
        const badgeRect = el.getBoundingClientRect();
        const miniCtaRect = el.querySelector<HTMLElement>('.tjba-hb-mini-cta')?.getBoundingClientRect();
        const hasVisibleMiniCta = Boolean(miniCtaRect && miniCtaRect.width > 0 && miniCtaRect.height > 0);
        const badgeLeft = hasVisibleMiniCta && miniCtaRect ? Math.min(badgeRect.left, miniCtaRect.left) : badgeRect.left;
        const badgeRight = hasVisibleMiniCta && miniCtaRect ? Math.max(badgeRect.right, miniCtaRect.right) : badgeRect.right;
        const headerRect = document.querySelector<HTMLElement>('header')?.getBoundingClientRect();
        const minimumTop = Math.max(24, headerRect ? headerRect.bottom - heroTopDoc + GAP_BELOW_BLOCKER : HEADER_FALLBACK_MIN_TOP);
        const blockers = Array.from(hero.querySelectorAll<HTMLElement>('[data-hero-blocker], [data-hero-scroll]'))
          .map((node) => node.getBoundingClientRect())
          .filter((r) => r.width > 0 && r.height > 0)
          .filter((r) => r.right + GAP_X > badgeLeft && r.left - GAP_X < badgeRight)
          .map((r) => [r.top - heroRect.top - GAP_BELOW_BLOCKER, r.bottom - heroRect.top + GAP_BELOW_BLOCKER] as const)
          .sort((a, b) => a[0] - b[0]);
        const visibleBottomInHero = Math.min(heroRect.height - 24, window.innerHeight - barH - gapAboveBar - heroTopDoc);
        let top = visibleBottomInHero - badgeH;
        let found = top >= minimumTop;

        for (let guard = 0; found && guard < 12; guard += 1) {
          const hit = blockers.find((b) => top < b[1] && top + badgeH > b[0]);
          if (!hit) break;
          top = hit[0] - badgeH;
          found = top >= minimumTop;
        }

        return { found, top, badgeH, heroH: heroRect.height };
      };

      let placement = computePlacement();
      if (!placement.found && window.innerWidth <= 768) {
        el.style.setProperty('--tjba-hb-cover-w', '84px');
        el.style.setProperty('--tjba-hb-card-w', '84px');
        placement = computePlacement();
      }

      if (placement.found) {
        if (window.innerWidth < 1440) {
          el.style.setProperty('--tjba-hb-top', `${Math.round(placement.top)}px`);
          el.style.setProperty('--tjba-hb-bottom', 'auto');
        } else {
          el.style.removeProperty('--tjba-hb-top');
          el.style.setProperty('--tjba-hb-bottom', `${Math.round(placement.heroH - placement.top - placement.badgeH)}px`);
        }
      } else {
        el.style.removeProperty('--tjba-hb-top');
        el.style.removeProperty('--tjba-hb-bottom');
      }
    };
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(place);
    };

    place();
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);
    document.fonts?.ready.then(onResize).catch(() => undefined);
    const transitionTimer = window.setTimeout(onResize, 1400);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(transitionTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
    };
  }, [lang]);

  return (
    <aside ref={ref} className="hero-book-badge tjba-hb" aria-label={isEn ? 'Supervised book' : '監修書籍'}>
      <a
        className="tjba-hb-link"
        href={PURCHASE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={isEn ? 'View the supervised book on Amazon' : '監修書籍をAmazonで見る'}
      >
        <span className="tjba-hb-float">
          <span className="tjba-hb-cover-wrap">
            <span className="tjba-hb-frame">
              <img
                className="hero-book-cover tjba-hb-cover"
                src="/images/book/gakken-butler-manners-cover.jpg"
                srcSet="/images/book/gakken-butler-manners-cover@2x.jpg 2x"
                width="480"
                height="705"
                alt={isEn ? COVER_ALT_EN : COVER_ALT_JA}
                loading="eager"
                decoding="async"
              />
            </span>
            <span className="tjba-hb-tag" aria-hidden="true">{isEn ? 'Supervised' : '監修書籍'}</span>
            <span className="tjba-hb-mini-cta">
              {isEn ? 'View on Amazon' : 'Amazonで見る'}
              <span className="tjba-hb-arw" aria-hidden="true">↗</span>
            </span>
          </span>
          <span className="hero-book-copy tjba-hb-copy">
            <span className="hero-book-eyebrow tjba-hb-eyebrow">
              {isEn ? 'Supervised by the Japan Butler Association' : '全日本執事協会 監修'}
            </span>
            <span className="hero-book-title tjba-hb-title">
              {isEn ? "A British Butler's Guide to Manners and Cultures (Gakken)" : '『執事のマナーと教養図鑑』(学研)'}
            </span>
            <span className="hero-book-summary tjba-hb-summary">
              {isEn ? 'Butler etiquette and culture in one volume.' : '執事の作法と教養を、一冊に。'}
            </span>
            <span className="tjba-hb-cta">
              {isEn ? 'View on Amazon' : 'Amazonで見る'}
              <span className="tjba-hb-arw" aria-hidden="true">↗</span>
            </span>
          </span>
        </span>
      </a>
    </aside>
  );
}
